import { useMemo, useState } from "react";
import { Check, Copy } from "lucide-react";
import type { TraceEvent } from "@/types/dashboard";
import { Button, Modal, Tabs } from "@/components/ui";
import { buildActivities } from "./trace/buildActivities";
import { buildCondensedTrace, extractResults, resolveEvaluation } from "./trace/helpers";
import { TraceTimeline } from "./trace/TraceTimeline";
import { ResultsPanel } from "./trace/EvaluationPanel";

interface TraceViewerProps {
  questionId: string;
  question: string;
  traces: TraceEvent[];
  sql?: string;
  answer?: string;
  evaluation?: {
    score: number;
    confidence?: string;
    critique?: string;
    reasoning?: string;
  };
  onClose: () => void;
}

type ViewTab = 'trace' | 'results';

export function TraceViewer({ questionId, question, traces, sql, answer, evaluation, onClose }: TraceViewerProps) {
  const [activeTab, setActiveTab] = useState<ViewTab>('trace');
  const [copied, setCopied] = useState(false);

  // Transform traces into activities
  const activities = useMemo(() => buildActivities(traces, question, sql, answer), [traces, question, sql, answer]);

  // Count tool calls (nested activities)
  const toolCallCount = useMemo(() => activities.filter(a => a.metadata?.isNested).length, [activities]);

  // Extract evaluation from traces if not provided
  const evaluationData = useMemo(() => resolveEvaluation(evaluation, traces), [evaluation, traces]);

  const results = useMemo(() => extractResults(traces, sql, answer, evaluationData), [traces, sql, answer, evaluationData]);

  const copyCondensedTrace = async () => {
    const text = buildCondensedTrace({ questionId, question, answer, sql, evaluationData, activities, traces });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const footerSummary =
    activeTab === 'trace' ? (
      <span>
        {activities.length} {activities.length === 1 ? 'stap' : 'stappen'} · {toolCallCount} tool-{toolCallCount === 1 ? 'aanroep' : 'aanroepen'}
      </span>
    ) : (
      <span>
        {[results.hasSql && '✓ SQL', results.hasAnswer && '✓ Antwoord', results.hasEval && '✓ Beoordeling'].filter(Boolean).join(' · ')}
      </span>
    );

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      className="h-[90vh]"
      bodyClassName="p-0"
      title={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          Vraagdetails
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-medium text-muted-foreground">{questionId}</span>
        </span>
      }
      description={<span className="line-clamp-2 text-foreground">{question}</span>}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {footerSummary}
            {activeTab === 'trace' && (
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 animate-pulse rounded-full bg-success" />
                Live bijgewerkt
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={copyCondensedTrace} title="Volledige trace kopiëren (platte tekst)">
            {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Gekopieerd' : 'Trace kopiëren'}
          </Button>
        </div>
      }
    >
      <div className="sticky top-0 z-10 border-b border-border bg-card px-5 py-3">
        <Tabs<ViewTab>
          value={activeTab}
          onChange={setActiveTab}
          items={[
            { value: 'trace', label: 'Trace', count: activities.length },
            { value: 'results', label: 'Resultaat' },
          ]}
        />
      </div>
      <div className="px-5 py-4">
        {activeTab === 'trace' ? (
          <TraceTimeline activities={activities} />
        ) : (
          <ResultsPanel cleanAnswer={results.cleanAnswer} finalSql={results.finalSql} finalEvaluation={results.finalEvaluation} />
        )}
      </div>
    </Modal>
  );
}
